import React, { useState, useEffect, useContext } from "react";
import { Pencil, Check, Trash2 } from "lucide-react";
import "./Price.css";
import { LanguageContext } from "../context/LanguageContext";

const AdditionalCosts = ({ objectId }) => {
  const [additionalCosts, setAdditionalCosts] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [availableCosts, setAvailableCosts] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [objectName, setObjectName] = useState("");
  const [currencyCode, setCurrencyCode] = useState("CHF");

  const { texts, language } = useContext(LanguageContext);

  // ---------------------------------------------------------
  // 1) Kostenarten & Einheiten laden (multilingual)
  // ---------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    const langParam = `?lang=${language}`;

    const fetchAvailableCosts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/cost_types${langParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error(texts.additionalCosts.errorLoadingTypes);

        const costs = await response.json();
        setAvailableCosts(costs);
      } catch (error) {
        console.error(texts.additionalCosts.errorLoadingTypes, error);
      }
    };

    const fetchAvailableUnits = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/cost_units${langParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error(texts.additionalCosts.errorLoadingUnits);

        const units = await response.json();
        setAvailableUnits(units);
      } catch (error) {
        console.error(texts.additionalCosts.errorLoadingUnits, error);
      }
    };

    fetchAvailableCosts();
    fetchAvailableUnits();
  }, [language]);

  // ---------------------------------------------------------
  // 2) Objektname, Nebenkosten & Währung laden
  // ---------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    const langParam = `?lang=${language}`;

    const fetchObjectName = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/objects`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error("Fehler beim Laden der Objekte");

        const objects = await response.json();
        const matched = objects.find((o) => String(o.id) === String(objectId));

        setObjectName(matched?.name || `${texts.additionalCosts.object} ${objectId}`);
      } catch (error) {
        console.error("Fehler beim Laden des Objektnamens:", error);
        setObjectName(`${texts.additionalCosts.object} ${objectId}`);
      }
    };

    const fetchAdditionalCosts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/object/${objectId}/additional-costs${langParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error(texts.additionalCosts.errorLoadingCosts);

        const costs = await response.json();

        const mapped = costs.map((c) => ({
          id: c.id,
          type: c.cost_type_id,
          price: c.price,
          unit: c.unit_id
        }));

        setAdditionalCosts(mapped);
      } catch (error) {
        console.error(texts.additionalCosts.errorLoadingCosts, error);
      }
    };

    const fetchCurrency = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/object/${objectId}/currency`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok) throw new Error("Fehler beim Abrufen der Währung");

        const data = await response.json();
        setCurrencyCode(data.currency || "CHF");
      } catch (error) {
        console.error("Fehler beim Abrufen der Währung:", error);
      }
    };

    fetchObjectName();
    fetchAdditionalCosts();
    fetchCurrency();
  }, [objectId, language]);

  // ---------------------------------------------------------
  // 3) Neue Kostenposition hinzufügen
  // ---------------------------------------------------------
  const handleAddAdditionalCost = () => {
    if (editingIndex !== null) {
      alert(texts.additionalCosts.finishEditingFirst);
      return;
    }

    const newCost = { type: "", price: "", unit: "", id: undefined };
    setAdditionalCosts((prev) => {
      const updatedCosts = [...prev, newCost];
      setEditingIndex(updatedCosts.length - 1);
      return updatedCosts;
    });
  };

  // ---------------------------------------------------------
  // 4) Kostenposition speichern
  // ---------------------------------------------------------
  const handleSaveAdditionalCost = async (index) => {
    const updatedCosts = [...additionalCosts];
    const currentCost = updatedCosts[index];

    if (!currentCost.type || !currentCost.price || !currentCost.unit) {
      alert(texts.additionalCosts.fillAllFields);
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem("authToken");

      const payload = {
        costTypeId: currentCost.type,
        price: currentCost.price,
        costUnitId: currentCost.unit
      };

      let response;

      if (currentCost.id) {
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/object/${objectId}/additional-costs/${currentCost.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          }
        );
      } else {
        response = await fetch(
          `${process.env.REACT_APP_API_URL}/object/${objectId}/additional-costs`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          }
        );
      }

      if (!response.ok) throw new Error(texts.additionalCosts.errorSaving);

      const saved = await response.json();

      updatedCosts[index] = {
        id: saved.id,
        type: saved.cost_type_id,
        price: saved.price,
        unit: saved.unit_id
      };

      setAdditionalCosts(updatedCosts);
      setEditingIndex(null);
    } catch (error) {
      console.error(texts.additionalCosts.errorSaving, error);
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------
  // 5) Kostenposition löschen
  // ---------------------------------------------------------
  const handleDeleteAdditionalCost = async (index) => {
    const confirmDelete = window.confirm(texts.additionalCosts.confirmDelete);
    if (!confirmDelete) return;

    const cost = additionalCosts[index];

    try {
      const token = localStorage.getItem("authToken");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/object/${objectId}/additional-costs/${cost.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error(texts.additionalCosts.errorDeleting);

      setAdditionalCosts(additionalCosts.filter((_, i) => i !== index));
    } catch (error) {
      console.error(texts.additionalCosts.errorDeleting, error);
    }
  };

  // ---------------------------------------------------------
  // 6) Hilfsfunktionen
  // ---------------------------------------------------------
  const handleChangeAdditionalCost = (index, field, value) => {
    const updatedCosts = [...additionalCosts];
    updatedCosts[index][field] = value;
    setAdditionalCosts(updatedCosts);
  };

  const getAvailableOptions = (currentTypeId = null) => {
    const used = additionalCosts
      .map((item, i) => (i !== editingIndex ? item.type : null))
      .filter((id) => id !== null);

    return availableCosts.filter(
      (opt) => !used.includes(opt.id) || opt.id === currentTypeId
    );
  };

  const isAddButtonDisabled = () => {
    const used = additionalCosts.map((i) => i.type);
    const all = availableCosts.map((c) => c.id);
    return used.length === all.length;
  };

  const getCostTypeName = (id) => {
  const found = availableCosts.find((c) => c.id === Number(id));
  return found ? found.name : "";
};

const getUnitName = (id) => {
  const found = availableUnits.find((u) => u.id === Number(id));
  return found ? found.name : "";
};


return (
  <div>
    <h2>
      {texts.additionalCosts.title}{" "}
      {objectName || `${texts.additionalCosts.object} ${objectId}`}
    </h2>

    {additionalCosts.length > 0 && (
      <div className="additional-costs-list">
        {additionalCosts.map((costItem, index) => (
          <div key={costItem.id || index} className="additional-cost-item">
            <div className="input">
              {editingIndex === index ? (
                <>
                  {/* Kostenart */}
                  <select
                    value={costItem.type || ""}
                    onChange={(e) =>
                      handleChangeAdditionalCost(index, "type", Number(e.target.value))
                    }
                  >
                    <option value="">{texts.additionalCosts.selectType}</option>
                    {getAvailableOptions(costItem.type).map((option) => (
                      <option key={option.id} value={option.id}>
                        {texts.additionalCosts.types?.[option.name] || option.name}
                      </option>
                    ))}
                  </select>

                  {/* Preis */}
                  <input
                    type="number"
                    className="number-input"
                    value={costItem.price || ""}
                    onChange={(e) =>
                      handleChangeAdditionalCost(index, "price", e.target.value)
                    }
                    placeholder={`${texts.additionalCosts.pricePlaceholder} ${currencyCode}`}
                  />

                  {/* Einheit */}
                  <select
                    value={costItem.unit || ""}
                    onChange={(e) =>
                      handleChangeAdditionalCost(index, "unit", Number(e.target.value))
                    }
                  >
                    <option value="">{texts.additionalCosts.selectUnit}</option>
                    {availableUnits.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.name}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  {/* Kostenart (Lesemodus) */}
                  <select value={costItem.type || ""} disabled>
                    <option value={costItem.type}>
                      {availableCosts.find(
                        (opt) => opt.id === Number(costItem.type)
                      )?.name || costItem.type}
                    </option>
                  </select>

                  {/* Preis (Lesemodus) */}
                  <input
                    type="text"
                    className="number-input"
                    value={
                      costItem.price ? `${costItem.price} ${currencyCode}` : ""
                    }
                    disabled
                  />

                  {/* Einheit (Lesemodus) */}
                  <select value={costItem.unit || ""} disabled>
                    <option value={costItem.unit}>
                      {availableUnits.find(
                        (u) => u.id === Number(costItem.unit)
                      )?.name || costItem.unit}
                    </option>
                  </select>
                </>
              )}
            </div>

            {/* BUTTONS */}
            <div className="button-container">
              {editingIndex === index ? (
                <>
                  <button
                    type="button"
                    className="save-button"
                    onClick={() => handleSaveAdditionalCost(index)}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? texts.additionalCosts.saving
                      : <Check size={18} strokeWidth={4} color="#f7f4f4ff" />
                    }

                  </button>

                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      if (!additionalCosts[index].id) {
                        setAdditionalCosts(
                          additionalCosts.filter((_, i) => i !== index)
                        );
                      }
                      setEditingIndex(null);
                    }}
                  >
                    {texts.additionalCosts.cancel}
                  </button>
                </>
              ) : (
                editingIndex === null && (
                  <>
                    <button
                      type="button"
                      className="edit-button"
                      onClick={() => setEditingIndex(index)}
                    >
                      <Pencil size={18} strokeWidth={1.5} color="#000" />
                    </button>

                    <button
                      type="button"
                      className="delete-button"
                      onClick={() => handleDeleteAdditionalCost(index)}
                    >
                      <Trash2 size={18} strokeWidth={1.5} color="#cc0000" />
                    </button>
                  </>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    )}

    <button
      onClick={handleAddAdditionalCost}
      disabled={editingIndex !== null || isAddButtonDisabled()}
      className="add-button"
    >
      + {texts.additionalCosts.add}
    </button>
  </div>
);


};

export default AdditionalCosts;
